import React, { useEffect, useState, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register the plugin only once
gsap.registerPlugin(ScrollTrigger);

const Counter = ({ endValue, duration = 2, suffix = "" }) => {
  // We'll use a local state only for display
  const [count, setCount] = useState(0);
  // Ref for the DOM element to trigger on
  const counterRef = useRef(null);
  // Ref for a plain JavaScript object that GSAP will tween
  const valueToAnimate = useRef({ value: 0 });

  // Note: GSAP duration is typically in seconds, changed from 2000ms to 2s

  useEffect(() => {
    // Select the current DOM node
    const element = counterRef.current;

    // Create the GSAP tween targetting the ref object
    const counterTween = gsap.to(valueToAnimate.current, {
      // Animate the 'value' property of the ref object from 0 to endValue
      value: endValue,
      // Use the provided duration (in seconds)
      duration: duration,
      // Easing function for the animation
      ease: "power1.out",
      // This function runs on every frame of the animation
      onUpdate: () => {
        // Update the React state with the current tweened value, rounded down
        setCount(Math.floor(valueToAnimate.current.value));
      },
      // Set the tween to be paused initially
      paused: true,
    });

    // Set up ScrollTrigger to control the tween
    ScrollTrigger.create({
      trigger: element,
      // Define when the animation should start (when the top of the element hits the bottom of the viewport)
      start: "top bottom",
      // When the trigger hits the start position...
      onEnter: () => {
        // Play the paused tween to start the counter animation
        counterTween.play();
      },
      // Ensure the animation only plays once (optional)
      once: true,
      // Clean up the trigger instance when the component unmounts
      // scrub: 0.5, // Uncomment this and remove onEnter/once for a scrubbing effect
    });

    // Cleanup function for GSAP
    return () => {
      // Kill the tween and any ScrollTriggers associated with it
      counterTween.kill();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [endValue, duration]); // Depend on endValue and duration

  // Format the number with commas (e.g., 15000 -> 15,000)
  const formattedCount = new Intl.NumberFormat().format(count);
  const isFinished = count >= endValue;

  return (
    <span ref={counterRef}>
      {formattedCount}
      {/* Show '+' while counting, show the final suffix when finished */}
      {!isFinished ? "+" : suffix}
    </span>
  );
};

export default Counter;
